import React, { useEffect, useMemo } from "react";
import styled from "styled-components";

import Button from "../button/Button";
import MediaPreview from "../media-preview/MediaPreview";
import ProgressBar from "../progress-bar/ProgressBar";
import StatusBadge from "../status-badge/StatusBadge";

/**
 * 再委託契約書 PDF のプレビュー状態。
 *
 * - `generating`: PDF を生成中（スケルトン + スピナー表示）
 * - `ready`     : 取得済（`MediaPreview` でインライン表示）
 * - `error`     : 生成失敗（エラー文 + 任意の再試行ボタン）
 */
/**
 * 契約書 PDF プレビューの取得済みデータ。
 * - `blob`: 動的生成（`direct_download`）のフォールバック取得。Object URL を内部で生成・破棄する。
 * - `url` : 保存済み PDF（Detail API の `contract_files[].url`）をそのまま表示する。
 */
export type SubcontractPdfPreviewState =
  | { status: "generating" }
  | { status: "ready"; blob: Blob; filename: string | null }
  | { status: "ready"; url: string; filename: string | null }
  | { status: "error"; errorMessage: string };

type Variant = "modal" | "message";

interface Props {
  preview: SubcontractPdfPreviewState;
  /** error 時に表示する「再試行」ボタンのハンドラ。省略時は表示しない。 */
  onRetry?: () => void;
  /**
   * PDF の左上に重ねるステータス（「締結済み」「無効」）。
   * 契約が更新されるとタイムラインに複数の版が並ぶため、どれが現在有効な
   * 契約書かを一目で判別できるようにする。
   */
  statusLabel?: string;
  /** 更新されて無効になった版。全体をグレーに沈めて現行版と区別する。 */
  isSuperseded?: boolean;
  /**
   * 表示形態。
   * - `modal`（既定）: モーダル内。高さ上限を設けて領域を取り過ぎないようにする
   * - `message`: タイムラインの添付。通常メッセージに PDF を送ったときと同じ
   *   幅・クリックでモーダル表示になる
   */
  variant?: "modal" | "message";
}

const Wrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Frame = styled.div<{ $superseded?: boolean; $variant: Variant }>`
  display: flex;
  align-items: center;
  /* ステータスバッジ（overlay）の位置基準 */
  position: relative;

  ${({ $variant }) =>
    $variant === "message"
      ? /* 通常メッセージの添付と同じ幅計算（コンテナ幅の 61.8%、256–720px）。
           高さ上限を持たせず要素内スクロールを作らない。 */
        `
    width: clamp(256px, 61.8cqi, 720px);
    max-width: 100%;
    justify-content: flex-start;
  `
      : `
    width: 100%;
    max-height: min(70dvh, 640px);
    justify-content: center;
  `}

  ${({ $superseded }) =>
    $superseded &&
    `
    filter: grayscale(1);
    opacity: 0.55;
  `}
`;

// `StatusBadge` の overlay は白文字・白枠で、暗いサムネイルに重ねる前提。
// 契約書 PDF は白地なので、読めるように半透明の暗い下敷きを足す。
const PdfStatusBadge = styled(StatusBadge)`
  background-color: rgba(0, 0, 0, 0.55);
`;

const SkeletonBox = styled.div`
  width: 100%;
  /* A4 に近いアスペクトで仮プレースホルダ */
  aspect-ratio: 1 / 1.414;
  max-height: min(70dvh, 640px);
  background: rgba(128, 128, 128, 0.08);
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--soremo-light-gray);
  padding: 24px;
`;

const ErrorBox = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1.414;
  max-height: min(70dvh, 640px);
  background: rgba(128, 128, 128, 0.04);
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  text-align: center;

  .material-symbols-rounded {
    font-size: 32px;
    color: var(--soremo-green);
  }
`;

const ErrorText = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-green);
  white-space: pre-wrap;
`;

/**
 * 再委託契約書 PDF プレビュー領域。
 *
 * `MediaPreview` (mediaType=`pdf`) で表示し、右下（プレビュー直下右寄せ）に
 * 3 点リーダーメニューを配置する（`menuOutside` で `MediaPreview` 直下の右側に
 * オーバーレイ）。`preview` は保存済み PDF の URL（`url`）か動的生成の Blob
 * （`blob`）のいずれかを持つ。Blob のときのみ Object URL を生成して渡し、
 * 保存済み URL はそのまま `MediaPreview` に渡す。
 */
const SubcontractContractPdfPreview: React.FC<Props> = ({
  preview,
  onRetry,
  statusLabel,
  isSuperseded = false,
  variant = "modal",
}) => {
  const blob =
    preview.status === "ready" && "blob" in preview ? preview.blob : null;
  const directUrl =
    preview.status === "ready" && "url" in preview ? preview.url : null;
  const filename = preview.status === "ready" ? preview.filename : null;
  // Blob のときだけ Object URL を生成・破棄する。保存済み URL はそのまま使う。
  const objectUrl = useMemo(
    () => (blob ? URL.createObjectURL(blob) : null),
    [blob],
  );
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);
  const src = objectUrl ?? directUrl;

  if (preview.status === "generating") {
    return (
      <Wrap>
        <SkeletonBox role="status" aria-label="契約書PDFを生成中">
          <div className="bodytext-13">契約書PDFを生成しています…</div>
          <ProgressBar width="60%" />
        </SkeletonBox>
      </Wrap>
    );
  }

  if (preview.status === "error") {
    return (
      <Wrap>
        <ErrorBox role="alert">
          <span className="material-symbols-rounded">error</span>
          <ErrorText>{preview.errorMessage}</ErrorText>
          {onRetry && (
            <Button
              size="small"
              variant="outlined"
              theme="secondary"
              onClick={onRetry}
            >
              再試行
            </Button>
          )}
        </ErrorBox>
      </Wrap>
    );
  }

  const isMessageVariant = variant === "message";

  return (
    <Wrap>
      <Frame $superseded={isSuperseded} $variant={variant}>
        {statusLabel && (
          <PdfStatusBadge variant="overlay" label={statusLabel} />
        )}
        {src &&
          (isMessageVariant ? (
            // 通常メッセージの PDF 添付と同じ表示（`MessageItem` の
            // `renderAttachedFiles`）。クリックでモーダルプレビューを開き、
            // 3 点メニューは出さない。
            <MediaPreview
              src={src}
              mediaType="pdf"
              fileName={filename ?? undefined}
              sizeMode={{ mode: "fixed-width", width: "100%", maxHeight: "100%" }}
              style={{ width: "100%" }}
              hoverPlay={false}
              disableHoverEffect
              enableClickPreview
              // 契約書は条文が続いたあと、巻末の別表に対価・納期などの要点が
              // 載る。1 ページ目を出しても中身が分からないので最終ページを見せる。
              pdfInitialPage="last"
            />
          ) : (
            <MediaPreview
              src={src}
              mediaType="pdf"
              fileName={filename ?? undefined}
              sizeMode={{ mode: "auto", maxHeight: "min(70dvh, 640px)" }}
              showMenu
              menuOutside
              disableHoverEffect
            />
          ))}
      </Frame>
    </Wrap>
  );
};

export default SubcontractContractPdfPreview;
